import { useId, useRef } from 'react';
import type { ReactNode } from 'react';

/**
 * SPEC-GAP: §6 lists Button, Chip, Card, FactRow, ParticipantChip, ComplianceNotice,
 * AnnouncementCard, MessageBubble, EmptyState and no tab set. Per §10 ("prefer the nearest
 * specified pattern") this is built from existing tokens only — `--text-label` for the tab label
 * exactly as FactRow uses it, `--color-text-primary` and `--color-border-subtle` as the rest of the
 * app uses them, `--tap-min` for the target height. No new colour, size or radius.
 *
 * **The selected tab is an ink underline plus `aria-selected`, never colour alone** — the same
 * reasoning §8 gives for the event palette: strip the hue and nothing becomes ambiguous.
 *
 * Roving tabindex: only the selected tab is in the tab order, and Left/Right/Home/End move between
 * them, which is what the ARIA pattern expects and what stops a five-tab bar costing five stops on
 * the way to the panel.
 */
export interface TabDefinition {
  /** Stable across renders — it keys the panel and the aria wiring. */
  id: string;
  label: string;
  /** Rendered after the label, e.g. a count. Kept out of the accessible name deliberately. */
  badge?: ReactNode;
  panel: ReactNode;
}

export function Tabs({
  tabs,
  selectedId,
  onSelect,
  /** Names the tablist for a screen reader — there is more than one region on these pages. */
  label,
}: {
  tabs: TabDefinition[];
  selectedId: string;
  onSelect: (id: string) => void;
  label: string;
}) {
  const base = useId();
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  // A single tab is not a choice, so it does not render as one. The panel stands alone rather than
  // under a bar that offers nothing -- a parent with one child going should not be asked to pick.
  if (tabs.length <= 1) {
    return <>{tabs[0]?.panel ?? null}</>;
  }

  const move = (to: number) => {
    const target = tabs[(to + tabs.length) % tabs.length];
    onSelect(target.id);
    refs.current[target.id]?.focus();
  };

  return (
    <div>
      <div
        role="tablist"
        aria-label={label}
        className="flex flex-wrap"
        style={{
          gap: 'var(--space-4)',
          borderBottom: '1px solid var(--color-border-subtle)',
          marginBottom: 'var(--space-4)',
        }}
      >
        {tabs.map((tab, index) => {
          const selected = tab.id === selectedId;

          return (
            <button
              key={tab.id}
              ref={(el) => {
                refs.current[tab.id] = el;
              }}
              type="button"
              role="tab"
              id={`${base}-tab-${tab.id}`}
              aria-controls={`${base}-panel-${tab.id}`}
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              onClick={() => onSelect(tab.id)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowRight') {
                  e.preventDefault();
                  move(index + 1);
                } else if (e.key === 'ArrowLeft') {
                  e.preventDefault();
                  move(index - 1);
                } else if (e.key === 'Home') {
                  e.preventDefault();
                  move(0);
                } else if (e.key === 'End') {
                  e.preventDefault();
                  move(tabs.length - 1);
                }
              }}
              className="t-label inline-flex items-center gap-2"
              style={{
                minHeight: 'var(--tap-min)',
                padding: '0 0 var(--space-2)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: selected ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
                // The underline is the selected state, in ink rather than a hue. It sits on
                // every tab, transparent when unselected, so choosing one shifts nothing.
                borderBottom: `3px solid ${selected ? 'var(--color-text-primary)' : 'transparent'}`,
                marginBottom: '-1px',
              }}
            >
              {tab.label}
              {tab.badge}
            </button>
          );
        })}
      </div>

      {tabs.map((tab) => (
        <div
          key={tab.id}
          role="tabpanel"
          id={`${base}-panel-${tab.id}`}
          aria-labelledby={`${base}-tab-${tab.id}`}
          hidden={tab.id !== selectedId}
          // Focusable so the panel is reachable straight from the tab, per the ARIA pattern.
          tabIndex={tab.id === selectedId ? 0 : -1}
        >
          {tab.id === selectedId && tab.panel}
        </div>
      ))}
    </div>
  );
}

/**
 * The docket panel's spine — TROOP-137.
 *
 * **Two words, and they are the same two in every panel.** Going, Forms and Bring each open onto
 * this bar: *Yours* is what this reader owes, *Everyone* is the unit. A leader learns one gesture
 * once rather than discovering that Going hides answering-for-others behind a disclosure while
 * Bring uses tabs and Forms interleaves both into every card — which is what the event page did,
 * and it is why nobody could tell where their own business ended and the troop's setup began.
 *
 * **The labels live here rather than at the three call sites**, for the same reason the unit's own
 * word for a patrol lives on `Unit`: three copies of a label is three chances for one of them to
 * say something slightly different, and a spine that reads "Yours / Everyone" in two panels and
 * "Mine / All" in the third is worse than no spine at all.
 *
 * **`everyone` is omitted, never disabled.** A plain member has no unit half — the API withholds
 * it, so a bar offering it would be a promise the server refuses. {@link Tabs} renders a lone panel
 * with no bar at all, so that reader simply gets their own things with no navigation to learn.
 * That is the whole "a normal member only sees what is asked of them" rule, expressed as an absence
 * rather than as a greyed-out control.
 *
 * "Everyone" rather than "The trip": an event is as often a Monday meeting as a campout, and the
 * repo has already been through this once with "packing" quietly narrowing a feature to trips with
 * tents.
 */
export function PanelTabs({
  label,
  selected,
  onSelect,
  yours,
  everyone,
}: {
  /** Names the tablist for a screen reader. "Who is going", "What this event asks for". */
  label: string;
  selected: string;
  onSelect: (id: string) => void;
  yours: ReactNode;
  /** Absent for anybody the server does not offer a unit-wide view to. */
  everyone?: ReactNode;
}) {
  return (
    <Tabs
      label={label}
      selectedId={selected}
      onSelect={onSelect}
      tabs={[
        { id: 'yours', label: 'Yours', panel: yours },
        ...(everyone ? [{ id: 'everyone', label: 'Everyone', panel: everyone }] : []),
      ]}
    />
  );
}
